import { useEffect, useId, useRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Info, Loader2, X } from "lucide-react";
import type { ApiError } from "../lib/api";
import { humanize, statusLabel, verificationLabel } from "../lib/format";
import { labelIn, useLang } from "../lib/i18n";

export function cx(...c: (string | false | null | undefined)[]): string {
  return c.filter(Boolean).join(" ");
}

// ----------------------------------------------------------------- Buttons
type Variant = "primary" | "secondary" | "ghost" | "danger" | "saffron" | "light";
const variants: Record<Variant, string> = {
  primary: "bg-ink-900 text-white hover:bg-ink-800 shadow-[0_1px_0_rgb(255_255_255/0.1)_inset,0_8px_20px_-10px_rgb(11_21_48/0.6)]",
  secondary: "bg-white text-ink-900 border hairline hover:bg-ink-50",
  ghost: "text-ink-700 hover:bg-ink-50",
  danger: "bg-red-700 text-white hover:bg-red-800",
  saffron: "bg-saffron-500 text-ink-950 hover:bg-saffron-400 shadow-[0_8px_24px_-10px_rgb(232_131_58/0.8)]",
  light: "bg-white/10 text-white border border-white/20 hover:bg-white/15 backdrop-blur",
};
const sizes = { sm: "h-8 px-3 text-[13px]", md: "h-10 px-4 text-sm", lg: "h-12 px-6 text-[15px]" };

export function Button({
  variant = "primary",
  size = "md",
  loading,
  className,
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant; size?: keyof typeof sizes; loading?: boolean }) {
  return (
    <button
      {...rest}
      disabled={rest.disabled || loading}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-full font-medium tracking-[-0.005em] transition-all duration-150 active:scale-[0.98] disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap cursor-pointer",
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
      {children}
    </button>
  );
}

export function ButtonLink({ to, variant = "primary", size = "md", className, children }: { to: string; variant?: Variant; size?: keyof typeof sizes; className?: string; children: ReactNode }) {
  const cls = cx("inline-flex items-center justify-center gap-2 rounded-full font-medium transition-all duration-150 active:scale-[0.98] whitespace-nowrap", variants[variant], sizes[size], className);
  if (to.startsWith("/auth/") || to.startsWith("http")) return <a href={to} className={cls}>{children}</a>;
  return <Link to={to} className={cls}>{children}</Link>;
}

// ----------------------------------------------------------------- Surfaces
export function Card({ className, children, as: As = "div" }: { className?: string; children: ReactNode; as?: any }) {
  return <As className={cx("card", className)}>{children}</As>;
}

export function PageHeader({ eyebrow, title, description, actions }: { eyebrow?: string; title: ReactNode; description?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
      <div className="max-w-3xl">
        {eyebrow && <div className="eyebrow text-saffron-600 mb-2">{eyebrow}</div>}
        <h1 className="text-[28px] sm:text-[34px] font-semibold tracking-tight text-ink-900 text-balance">{title}</h1>
        {description && <p className="mt-2 text-[15px] leading-relaxed text-ink-500">{description}</p>}
      </div>
      {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
    </div>
  );
}

export function SectionTitle({ children, aside }: { children: ReactNode; aside?: ReactNode }) {
  return (
    <div className="mb-3 flex items-center justify-between gap-3">
      <h2 className="text-[15px] font-semibold text-ink-900">{children}</h2>
      {aside}
    </div>
  );
}

// ----------------------------------------------------------------- Badges
type Tone = "neutral" | "navy" | "sage" | "saffron" | "red" | "amber" | "blue";
const tones: Record<Tone, string> = {
  neutral: "bg-ink-50 text-ink-600 ring-ink-200/70",
  navy: "bg-ink-900 text-white ring-ink-900",
  sage: "bg-sage-50 text-sage-700 ring-sage-200",
  saffron: "bg-saffron-50 text-saffron-700 ring-saffron-200",
  red: "bg-red-50 text-red-700 ring-red-200",
  amber: "bg-amber-50 text-amber-800 ring-amber-200",
  blue: "bg-sky-50 text-sky-800 ring-sky-200",
};
export function Badge({ tone = "neutral", children, className }: { tone?: Tone; children: ReactNode; className?: string }) {
  return <span className={cx("inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[12px] font-medium ring-1 ring-inset whitespace-nowrap", tones[tone], className)}>{children}</span>;
}

const STATUS_TONE: Record<string, Tone> = {
  submitted: "neutral",
  under_review: "blue",
  assigned: "blue",
  in_progress: "amber",
  escalated: "saffron",
  resolved: "sage",
  closed: "neutral",
  reopened: "saffron",
};
export function StatusBadge({ status }: { status: string }) {
  return <Badge tone={STATUS_TONE[status] ?? "neutral"}>{statusLabel(status)}</Badge>;
}
const VER_TONE: Record<string, Tone> = { unverified: "neutral", evidence_reviewed: "blue", verified: "sage", disputed: "amber", unsubstantiated: "red" };
export function VerificationBadge({ status }: { status: string }) {
  return <Badge tone={VER_TONE[status] ?? "neutral"}>{verificationLabel(status)}</Badge>;
}
export function AllegationBadge() {
  const { tx } = useLang();
  return (
    <Badge tone="saffron" className="font-semibold">
      {tx("Allegation — not a finding", "आरोप — फ़ैसला नहीं")}
    </Badge>
  );
}

// ----------------------------------------------------------------- Feedback
export function Spinner({ className }: { className?: string }) {
  const { tx } = useLang();
  return <Loader2 className={cx("h-5 w-5 animate-spin text-ink-400", className)} aria-label={tx("Loading", "लोड हो रहा है")} />;
}

export function Loading({ label }: { label?: string }) {
  const { tx } = useLang();
  return (
    <div className="flex items-center justify-center gap-3 py-16 text-sm text-ink-500" role="status">
      <Spinner />
      {label ?? tx("Loading…", "लोड हो रहा है…")}
    </div>
  );
}

export function Notice({ tone = "info", title, children, className }: { tone?: "info" | "warn" | "success" | "error"; title?: string; children?: ReactNode; className?: string }) {
  const styles = {
    info: ["bg-ink-50 border-ink-100 text-ink-700", Info],
    warn: ["bg-amber-50 border-amber-200 text-amber-900", AlertTriangle],
    success: ["bg-sage-50 border-sage-200 text-sage-900", CheckCircle2],
    error: ["bg-red-50 border-red-200 text-red-900", AlertTriangle],
  } as const;
  const [cls, Icon] = styles[tone];
  return (
    <div className={cx("flex gap-3 rounded-2xl border p-4 text-sm leading-relaxed", cls, className)} role={tone === "error" ? "alert" : "status"}>
      <Icon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <div>
        {title && <div className="font-semibold">{title}</div>}
        {children && <div className={title ? "mt-0.5 opacity-90" : ""}>{children}</div>}
      </div>
    </div>
  );
}

export function ErrorNote({ error, className }: { error: ApiError | Error | null | undefined; className?: string }) {
  const { tx } = useLang();
  if (!error) return null;
  const e = error as ApiError;
  const title =
    e.code === "mfa_required"
      ? tx("Additional verification required", "एक और पुष्टि ज़रूरी है")
      : e.status === 403
        ? tx("Not permitted", "इसकी इजाज़त नहीं है")
        : e.status === 404
          ? tx("Not found", "नहीं मिला")
          : tx("Something went wrong", "कुछ गड़बड़ हो गई");
  return (
    <Notice tone="error" title={title} className={className}>
      {error.message}
      {Object.keys(e.fields ?? {}).length > 0 && (
        <ul className="mt-1 list-disc pl-5">
          {Object.entries(e.fields).map(([field, msg]) => (
            <li key={field}>
              <span className="font-medium">{humanize((field.split(".").pop() || field).replace(/([a-z])([A-Z])/g, "$1 $2").toLowerCase())}:</span> {msg}
            </li>
          ))}
        </ul>
      )}
      {e.code === "mfa_required" && (
        <>
          {" "}
          <Link className="underline font-medium" to="/security">
            {tx("Verify now", "अभी पुष्टि करें")}
          </Link>
        </>
      )}
    </Notice>
  );
}

export function EmptyState({ icon, title, children, action }: { icon?: ReactNode; title: string; children?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-[var(--radius-card)] border border-dashed hairline bg-white/60 px-6 py-14 text-center">
      {icon && <div className="mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-ink-50 text-ink-500">{icon}</div>}
      <div className="text-[15px] font-semibold text-ink-900">{title}</div>
      {children && <div className="mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">{children}</div>}
      {action && <div className="mt-5">{action}</div>}
    </div>
  );
}

// ----------------------------------------------------------------- Forms
export function Field({ label, hint, error, required, children, className }: { label: ReactNode; hint?: ReactNode; error?: string; required?: boolean; children: (id: string, invalid: boolean) => ReactNode; className?: string }) {
  const id = useId();
  return (
    <div className={className}>
      <label htmlFor={id} className="mb-1.5 block text-[13px] font-medium text-ink-800">
        {label}
        {required && <span className="ml-0.5 text-saffron-600" aria-hidden>*</span>}
      </label>
      {children(id, Boolean(error))}
      {error ? (
        <p className="mt-1.5 text-[12.5px] text-red-700" role="alert">
          {error}
        </p>
      ) : hint ? (
        <p className="mt-1.5 text-[12.5px] leading-relaxed text-ink-500">{hint}</p>
      ) : null}
    </div>
  );
}

export function TextInput({ label, hint, error, required, value, onChange, className, ...rest }: { label: ReactNode; hint?: ReactNode; error?: string; required?: boolean; value: string; onChange: (v: string) => void; className?: string } & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange">) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {(id, invalid) => <input id={id} className="field" aria-invalid={invalid} required={required} value={value} onChange={(e) => onChange(e.target.value)} {...rest} />}
    </Field>
  );
}

export function TextArea({ label, hint, error, required, value, onChange, rows = 4, className, ...rest }: { label: ReactNode; hint?: ReactNode; error?: string; required?: boolean; value: string; onChange: (v: string) => void; rows?: number; className?: string } & Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange">) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {(id, invalid) => <textarea id={id} className="field resize-y" rows={rows} aria-invalid={invalid} required={required} value={value} onChange={(e) => onChange(e.target.value)} {...rest} />}
    </Field>
  );
}

export function Select({ label, hint, error, required, value, onChange, options, placeholder, className }: { label: ReactNode; hint?: ReactNode; error?: string; required?: boolean; value: string; onChange: (v: string) => void; options: ({ value: string; label: string } | string)[]; placeholder?: string; className?: string }) {
  return (
    <Field label={label} hint={hint} error={error} required={required} className={className}>
      {(id, invalid) => (
        <select id={id} className="field appearance-none bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 24 24%22 fill=%22none%22 stroke=%22%234a5a7d%22 stroke-width=%222%22><path d=%22m6 9 6 6 6-6%22/></svg>')] bg-[length:14px] bg-[right_0.9rem_center] bg-no-repeat pr-9" aria-invalid={invalid} required={required} value={value} onChange={(e) => onChange(e.target.value)}>
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => {
            const opt = typeof o === "string" ? { value: o, label: labelIn(o) } : o;
            return (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            );
          })}
        </select>
      )}
    </Field>
  );
}

export function Checkbox({ checked, onChange, children, error }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode; error?: string }) {
  return (
    <div>
      <label className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-ink-700">
        <input type="checkbox" className="mt-0.5 h-[18px] w-[18px] shrink-0 cursor-pointer rounded accent-ink-900" checked={checked} onChange={(e) => onChange(e.target.checked)} aria-invalid={Boolean(error)} />
        <span>{children}</span>
      </label>
      {error && <p className="mt-1.5 text-[12.5px] text-red-700">{error}</p>}
    </div>
  );
}

export function ChoiceCard({ selected, onClick, title, description, icon }: { selected: boolean; onClick: () => void; title: ReactNode; description?: ReactNode; icon?: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      className={cx(
        "group w-full rounded-2xl border p-4 text-left transition-all cursor-pointer",
        selected ? "border-ink-900 bg-ink-900 text-white shadow-[var(--shadow-lift)]" : "hairline bg-white hover:border-ink-300 hover:shadow-[var(--shadow-soft)]",
      )}
    >
      <div className="flex items-start gap-3">
        {icon && <div className={cx("grid h-9 w-9 shrink-0 place-items-center rounded-xl", selected ? "bg-white/10" : "bg-ink-50 text-ink-700")}>{icon}</div>}
        <div>
          <div className="text-[14.5px] font-semibold">{title}</div>
          {description && <div className={cx("mt-1 text-[13px] leading-relaxed", selected ? "text-ink-200" : "text-ink-500")}>{description}</div>}
        </div>
      </div>
    </button>
  );
}

// ----------------------------------------------------------------- Modal
export function Modal({ open, onClose, title, children, footer, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; footer?: ReactNode; wide?: boolean }) {
  const { tx } = useLang();
  const ref = useRef<HTMLDivElement>(null);
  // Callers pass a fresh onClose on every render; reading it through a ref keeps
  // the effect below to open/close only, so typing never moves focus back out.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    const first = ref.current?.querySelector<HTMLElement>("input,select,textarea") ?? ref.current?.querySelector<HTMLElement>("button");
    first?.focus();
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && closeRef.current();
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
      prev?.focus();
    };
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/40 p-0 backdrop-blur-sm sm:items-center sm:p-6" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} className={cx("max-h-[92vh] w-full overflow-y-auto rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl", wide ? "sm:max-w-3xl" : "sm:max-w-lg")} style={{ animation: "rise .25s cubic-bezier(.2,.7,.2,1)" }}>
        <div className="sticky top-0 z-10 flex items-center justify-between border-b hairline bg-white/95 px-6 py-4 backdrop-blur">
          <h2 className="text-[16px] font-semibold">{title}</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-ink-400 hover:bg-ink-50 hover:text-ink-800 cursor-pointer" aria-label={tx("Close", "बंद करें")}>
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="px-6 py-5">{children}</div>
        {footer && <div className="sticky bottom-0 flex justify-end gap-2 border-t hairline bg-white/95 px-6 py-4 backdrop-blur">{footer}</div>}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------- Data display
export function Stat({ label, value, hint, tone }: { label: string; value: ReactNode; hint?: ReactNode; tone?: "default" | "saffron" | "sage" }) {
  return (
    <div className="card p-5">
      <div className="text-[12.5px] font-medium text-ink-500">{label}</div>
      <div className={cx("mt-2 text-[28px] font-semibold tracking-tight tabular-nums", tone === "saffron" ? "text-saffron-600" : tone === "sage" ? "text-sage-600" : "text-ink-900")}>{value}</div>
      {hint && <div className="mt-1 text-[12px] leading-snug text-ink-400">{hint}</div>}
    </div>
  );
}

export function Table({ head, children, empty }: { head: ReactNode[]; children: ReactNode; empty?: ReactNode }) {
  const hasRows = Array.isArray(children) ? children.length > 0 : Boolean(children);
  return (
    <div className="card overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-[13.5px]">
          <thead className="bg-ink-50/60 text-[12px] uppercase tracking-wide text-ink-500">
            <tr>
              {head.map((h, i) => (
                <th key={i} className="px-4 py-3 font-medium whitespace-nowrap">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-ink-100">{children}</tbody>
        </table>
      </div>
      {!hasRows && empty && <div className="p-6">{empty}</div>}
    </div>
  );
}

export function Tabs<T extends string>({ value, onChange, tabs }: { value: T; onChange: (v: T) => void; tabs: { value: T; label: string; count?: number }[] }) {
  return (
    <div className="mb-5 inline-flex flex-wrap gap-1 rounded-full bg-ink-50 p-1" role="tablist">
      {tabs.map((t) => (
        <button
          key={t.value}
          role="tab"
          aria-selected={value === t.value}
          onClick={() => onChange(t.value)}
          className={cx("rounded-full px-4 py-1.5 text-[13px] font-medium transition-all cursor-pointer", value === t.value ? "bg-white text-ink-900 shadow-sm" : "text-ink-500 hover:text-ink-800")}
        >
          {t.label}
          {t.count !== undefined && <span className="ml-1.5 text-ink-400 tabular-nums">{t.count}</span>}
        </button>
      ))}
    </div>
  );
}

export function KeyValue({ items }: { items: [ReactNode, ReactNode][] }) {
  return (
    <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2">
      {items.map(([k, v], i) => (
        <div key={i}>
          <dt className="text-[12px] font-medium uppercase tracking-wide text-ink-400">{k}</dt>
          <dd className="mt-0.5 text-[14px] text-ink-800 break-words">{v || "—"}</dd>
        </div>
      ))}
    </dl>
  );
}

export function SyntheticBanner() {
  const { tx } = useLang();
  return (
    <div className="bg-saffron-500 px-4 py-1.5 text-center text-[12.5px] font-medium text-ink-950">
      {tx("Demonstration environment — every person, place and record here is synthetic.", "यह डेमो साइट है — यहाँ हर व्यक्ति, जगह और रिकॉर्ड नक़ली है।")}
    </div>
  );
}
